import type { MarkdownCountdownEditRequest } from './markdown-countdown';
import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { MarkdownImageEditRequest } from './markdown-image-node';
import type { GalleryEditRequest } from './tools/gallery/editorjs-gallery-tool';

import { Box, Text } from '@chakra-ui/react';
import Link from '@tiptap/extension-link';
import { Markdown } from '@tiptap/markdown';
import { TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './markdown-editor.scss';

import { MarkdownCountdownEditModal } from './markdown-countdown-edit-modal';
import { MarkdownCountdownNode } from './markdown-countdown-node';
import {
  createDefaultMarkdownDocumentValue,
  getDocumentTitleTagValidationMessage,
} from './markdown-document-title';
import { MarkdownDocumentTitleNode } from './markdown-document-title-node';
import { MarkdownFilesNode } from './markdown-files-node';
import { MarkdownGalleryNode } from './markdown-gallery-node';
import { MarkdownHighlightMenuPopover } from './markdown-highlight-menu-popover';
import { MarkdownImageEditModal } from './markdown-image-edit-modal';
import { MarkdownImageNode } from './markdown-image-node';
import { MarkdownSlashMenuExtension } from './markdown-slash-menu';
import { MarkdownSlashMenuPopover } from './markdown-slash-menu-popover';

interface MarkdownEditorProps {
  documentId?: string;
  apiHost: string;
  userEmail: string;
  value: string;
  onChange: (value: string) => void;
  parentPath: string;
  children: Array<{ id: string; path: string; label?: string }>;
  editorPlaceholderMuted?: boolean;
  isNewDocument: boolean;
  onCreateSubpage?: () => void;
  onNavigateToDocument?: (path: string) => void;
  onRequestRename?: () => void;
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
}

function placeSelectionAfterDocumentTitle(
  editor: NonNullable<ReturnType<typeof useEditor>>,
) {
  const { doc } = editor.state;
  const titleNode = doc.firstChild;
  if (!titleNode || titleNode.type.name !== 'npDocumentTitle') {
    return;
  }

  const nextSelection = TextSelection.near(
    doc.resolve(Math.min(titleNode.nodeSize, doc.content.size)),
    1,
  );

  if (
    nextSelection.from === editor.state.selection.from &&
    nextSelection.to === editor.state.selection.to
  ) {
    return;
  }

  editor.view.dispatch(editor.state.tr.setSelection(nextSelection));
}

export function MarkdownEditor({
  documentId,
  apiHost,
  userEmail,
  value,
  onChange,
  parentPath,
  children,
  editorPlaceholderMuted = false,
  isNewDocument,
  onCreateSubpage,
  onNavigateToDocument,
  onRequestRename,
  onRequestGalleryEdit,
}: MarkdownEditorProps) {
  const contextRef = useRef<MarkdownEditorContextValue>({
    documentId,
    apiHost,
    userEmail,
    parentPath,
    childDocuments: children,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
    onRequestGalleryEdit,
  });
  const [imageEditRequest, setImageEditRequest] =
    useState<MarkdownImageEditRequest | null>(null);
  const [countdownEditRequest, setCountdownEditRequest] =
    useState<MarkdownCountdownEditRequest | null>(null);
  const handleRequestImageEdit = useCallback(
    (request: MarkdownImageEditRequest) => {
      setImageEditRequest(request);
    },
    [],
  );
  const handleRequestCountdownEdit = useCallback(
    (request: MarkdownCountdownEditRequest) => {
      setCountdownEditRequest(request);
    },
    [],
  );
  contextRef.current = {
    documentId,
    apiHost,
    userEmail,
    parentPath,
    childDocuments: children,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
    onRequestGalleryEdit,
    onRequestImageEdit: handleRequestImageEdit,
    onRequestCountdownEdit: handleRequestCountdownEdit,
  };

  const [validationMessage, setValidationMessage] = useState<string | null>(
    getDocumentTitleTagValidationMessage(value),
  );
  const initialContent =
    value.length > 0
      ? value
      : isNewDocument
        ? createDefaultMarkdownDocumentValue()
        : '';

  const extensions = useMemo(
    () => [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
      Markdown.configure({
        markedOptions: { gfm: true },
      }),
      MarkdownDocumentTitleNode.configure({ contextRef }),
      MarkdownImageNode.configure({ contextRef }),
      MarkdownCountdownNode.configure({ contextRef }),
      MarkdownFilesNode.configure({ contextRef }),
      MarkdownGalleryNode.configure({ contextRef }),
      MarkdownSlashMenuExtension.configure({
        onRequestImageEdit: handleRequestImageEdit,
        onRequestCountdownEdit: handleRequestCountdownEdit,
      }),
    ],
    [handleRequestCountdownEdit, handleRequestImageEdit],
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    contentType: 'markdown',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'markdown-editor-content',
      },
    },
    onCreate: ({ editor }) => {
      setValidationMessage(
        getDocumentTitleTagValidationMessage(editor.getMarkdown()),
      );
      if (isNewDocument && value.length === 0) {
        const seeded = createDefaultMarkdownDocumentValue();
        editor.commands.setContent(seeded, {
          contentType: 'markdown',
          emitUpdate: false,
        });
      }

      placeSelectionAfterDocumentTitle(editor);
    },
    onUpdate: ({ editor }) => {
      const next = editor.getMarkdown();
      onChange(next);
      setValidationMessage(getDocumentTitleTagValidationMessage(next));
    },
  });

  useEffect(() => {
    if (!editor) return;

    const nextValue =
      value.length > 0
        ? value
        : isNewDocument
          ? createDefaultMarkdownDocumentValue()
          : '';
    const currentValue = editor.getMarkdown();
    if (currentValue === nextValue) {
      setValidationMessage(getDocumentTitleTagValidationMessage(currentValue));
      return;
    }

    editor.commands.setContent(nextValue, {
      contentType: 'markdown',
      emitUpdate: false,
    });
    placeSelectionAfterDocumentTitle(editor);
    setValidationMessage(getDocumentTitleTagValidationMessage(nextValue));
  }, [editor, isNewDocument, value]);

  return (
    <div className="markdown-editor-root">
      <MarkdownImageEditModal
        request={imageEditRequest}
        onDismiss={() => setImageEditRequest(null)}
      />
      <MarkdownCountdownEditModal
        request={countdownEditRequest}
        onDismiss={() => setCountdownEditRequest(null)}
      />
      {validationMessage ? (
        <Box
          px={4}
          py={3}
          bg="orange.50"
          borderBottomWidth="1px"
          borderColor="orange.200"
        >
          <Text fontSize="sm" color="orange.700">
            {validationMessage}
          </Text>
        </Box>
      ) : null}

      <div className="markdown-editor-surface">
        <MarkdownHighlightMenuPopover editor={editor} />
        <MarkdownSlashMenuPopover
          editor={editor}
          onRequestCountdownEdit={handleRequestCountdownEdit}
          onRequestImageEdit={handleRequestImageEdit}
        />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
